import { isAxiosError, isCancel, type AxiosInstance } from "axios";
import type {
  GetDirectoryEntryOptions,
  ConnectionRequestDirectionType,
  RespondToRequestActionType,
} from "./directory-repository-options.types";
import {
  ConnectionRequestIdPublic,
  EventParticipantIdPublic,
  EventIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { LocaleCode } from "@shared/schemas/locale.schemas";
import type { SortDirectionType } from "@shared/schemas/pagination-options.schemas";
import { ActorAuthorizationErrorCodes } from "@shared/constants/actor-authorization-errors.constants";
import type { SendDirectoryExportResult } from "@shared/schemas/directory-export.schemas";
import type { IntroOriginDataType } from "@shared/schemas/intro-origin.schemas";
import { extractApiErrorCode } from "@frontend/shared/infrastructure/errors/error.utils";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import type { Paginated } from "@shared/schemas/paginated.schemas";
import type { DirectorySearchMode } from "@shared/schemas/directory-search-mode.schemas";

export interface GetProfileDetailApiResponse {
  readonly participantDto: unknown;
  readonly introsItems: readonly unknown[];
}

export interface GetEntriesParams {
  readonly eventIdPublic: EventIdPublic;
  readonly tab?: string;
  readonly searchQuery?: string;
  readonly searchMode?: DirectorySearchMode;
  readonly page?: number;
  readonly limit?: number;
  readonly sortBy?: string;
  readonly sortOrder?: SortDirectionType;
}

export interface GetDirectoryEntriesBatchParams {
  readonly eventIdPublic: EventIdPublic;
  readonly participantIdsPublic: readonly EventParticipantIdPublic[];
}

export interface CreateExportParams {
  readonly eventIdPublic: EventIdPublic;
}

export interface GetConnectionRequestsParams {
  readonly eventIdPublic: EventIdPublic;
  readonly direction?: ConnectionRequestDirectionType;
  readonly sortBy?: string;
  readonly sortOrder?: SortDirectionType;
}

export interface GetConnectedProfilesParams {
  readonly eventIdPublic: EventIdPublic;
  readonly sortBy?: string;
  readonly sortOrder?: SortDirectionType;
}

export interface GetProfileDetailParams {
  readonly eventIdPublic: EventIdPublic;
  readonly participantIdPublic: EventParticipantIdPublic;
}

export interface RequestIntroParams {
  readonly eventIdPublic: EventIdPublic;
  readonly targetParticipantIdPublic: EventParticipantIdPublic;
  readonly message?: string;
  readonly origin: string;
  readonly originData: IntroOriginDataType | null;
}

export interface RespondToRequestParams {
  readonly eventIdPublic: EventIdPublic;
  readonly requestIdPublic: string;
  readonly action: RespondToRequestActionType;
}

export interface MarkIntroSeenParams {
  readonly eventIdPublic: EventIdPublic;
  readonly requestIdPublic: string;
}

export interface GetContactInfoParams {
  readonly eventIdPublic: EventIdPublic;
  readonly requestIdPublic: ConnectionRequestIdPublic;
}

export interface GetSuggestionsParams {
  readonly eventIdPublic: EventIdPublic;
  readonly participantIdPublic: EventParticipantIdPublic;
}

// Wraps an intros API call so the targeted "actor is not a participant"
// 403 (host/admin/owner viewing participant-scoped endpoints) returns an
// empty page instead of throwing. Matched by the backend-supplied
// `ACTOR_NOT_PARTICIPANT` code rather than the raw 403 status so other
// 403 causes (auth revocation, IntroNotAuthorizedToView, future
// authorization rules) keep propagating.
async function introsOrEmpty<T>(
  call: Promise<{ data: Paginated<T> }>,
): Promise<{ data: Paginated<T> }> {
  try {
    return await call;
  } catch (error) {
    if (
      isAxiosError(error) &&
      error.response?.status === 403 &&
      extractApiErrorCode(error) ===
        ActorAuthorizationErrorCodes.ACTOR_NOT_PARTICIPANT
    ) {
      return {
        data: { items: [], total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw error;
  }
}

export function createDirectoryApi(apiClient: AxiosInstance) {
  return {
    async getEntries(
      params: GetEntriesParams,
      signal?: AbortSignal,
    ): Promise<Paginated<unknown>> {
      try {
        const queryParams = new URLSearchParams();

        if (
          params.searchQuery !== undefined &&
          params.searchQuery.trim() !== ""
        ) {
          queryParams.set("q", params.searchQuery.trim());

          if (params.searchMode !== undefined) {
            queryParams.set("searchMode", params.searchMode);
          }
        }

        if (params.page !== undefined) {
          queryParams.set("page", params.page.toString());
        }

        if (params.limit !== undefined) {
          queryParams.set("limit", params.limit.toString());
        }

        if (params.sortBy !== undefined) {
          queryParams.set("sortBy", params.sortBy);
        }

        if (params.sortOrder !== undefined) {
          queryParams.set("sortOrder", params.sortOrder);
        }

        const queryString = queryParams.toString();
        const url = `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/directory-entries${queryString !== "" ? `?${queryString}` : ""}`;
        const response = await apiClient.get<Paginated<unknown>>(url, {
          ...(signal !== undefined && { signal }),
        });

        return response.data;
      } catch (error) {
        if (isCancel(error)) throw error;
        applicationLogger.error(
          "Failed to fetch directory entries",
          "DirectoryHttpApi.getEntries",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "eventIdPublic", value: params.eventIdPublic },
            { key: "searchQuery", value: params.searchQuery ?? "none" },
          ],
        );
        throw error;
      }
    },

    async getDirectoryEntry(
      params: Omit<GetDirectoryEntryOptions, "enabled">,
    ): Promise<unknown> {
      try {
        const response = await apiClient.get<Paginated<unknown>>(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/directory-entries/${encodeURIComponent(EventParticipantIdPublic.unwrap(params.participantIdPublic))}`,
        );
        return response.data.items[0] ?? null;
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch directory entry",
          "DirectoryHttpApi.getDirectoryEntry",
          error instanceof Error ? error : new Error(String(error)),
          [{ key: "participantIdPublic", value: params.participantIdPublic }],
        );
        throw error;
      }
    },

    async getDirectoryEntriesBatch(
      params: GetDirectoryEntriesBatchParams,
    ): Promise<Paginated<unknown>> {
      try {
        const url = `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/directory-entries?mode=batch`;
        const response = await apiClient.post<Paginated<unknown>>(url, {
          participantIdsPublic: params.participantIdsPublic,
        });

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch directory entries batch",
          "DirectoryHttpApi.getDirectoryEntriesBatch",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "eventIdPublic", value: params.eventIdPublic },
            {
              key: "participantCount",
              value: String(params.participantIdsPublic.length),
            },
          ],
        );
        throw error;
      }
    },

    async createExport(
      params: CreateExportParams,
    ): Promise<SendDirectoryExportResult> {
      try {
        const response = await apiClient.post<SendDirectoryExportResult>(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/directory-entries/exports`,
        );

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to send the directory export",
          "DirectoryHttpApi.createExport",
          error instanceof Error ? error : new Error(String(error)),
          [{ key: "eventIdPublic", value: params.eventIdPublic }],
        );
        throw error;
      }
    },

    async getConnectedProfiles(params: GetConnectedProfilesParams): Promise<{
      readonly received: readonly unknown[];
      readonly sent: readonly unknown[];
    }> {
      try {
        const introsBase = `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros`;

        const baseQuery = new URLSearchParams();
        baseQuery.set("includeAccepted", "true");
        if (params.sortBy !== undefined) {
          baseQuery.set("sortBy", params.sortBy);
        }
        if (params.sortOrder !== undefined) {
          baseQuery.set("sortOrder", params.sortOrder);
        }

        const receivedQuery = new URLSearchParams(baseQuery);
        receivedQuery.set("direction", "received");
        const sentQuery = new URLSearchParams(baseQuery);
        sentQuery.set("direction", "sent");

        const [receivedResponse, sentResponse] = await Promise.all([
          introsOrEmpty(
            apiClient.get<Paginated<unknown>>(
              `${introsBase}?${receivedQuery.toString()}`,
            ),
          ),
          introsOrEmpty(
            apiClient.get<Paginated<unknown>>(
              `${introsBase}?${sentQuery.toString()}`,
            ),
          ),
        ]);

        return {
          received: receivedResponse.data.items,
          sent: sentResponse.data.items,
        };
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch connected profiles",
          "DirectoryHttpApi.getConnectedProfiles",
          error instanceof Error ? error : new Error(String(error)),
          [{ key: "eventIdPublic", value: params.eventIdPublic }],
        );
        throw error;
      }
    },

    async getProfileDetail(
      params: GetProfileDetailParams,
    ): Promise<GetProfileDetailApiResponse> {
      try {
        const [dirResponse, introsResponse] = await Promise.all([
          apiClient.get<Paginated<unknown>>(
            `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/directory-entries/${encodeURIComponent(EventParticipantIdPublic.unwrap(params.participantIdPublic))}`,
          ),
          introsOrEmpty(
            apiClient.get<Paginated<unknown>>(
              `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros?direction=all&includeAccepted=true&includeDeclined=true&participantIdPublic=${EventParticipantIdPublic.unwrap(params.participantIdPublic)}`,
            ),
          ),
        ]);

        return {
          participantDto: dirResponse.data.items[0],
          introsItems: introsResponse.data.items,
        };
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch profile detail",
          "DirectoryHttpApi.getProfileDetail",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "eventIdPublic", value: params.eventIdPublic },
            { key: "participantIdPublic", value: params.participantIdPublic },
          ],
        );
        throw error;
      }
    },

    // The whole page, not just its items: the deck and whether a run is about to
    // replace it arrive together, and unwrapping here would drop the half that
    // is about the page rather than any row in it. Unknown because the
    // repository owns the parse.
    async getSuggestions(params: GetSuggestionsParams): Promise<unknown> {
      try {
        const response = await apiClient.get<unknown>(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/participants/${encodeURIComponent(EventParticipantIdPublic.unwrap(params.participantIdPublic))}/suggestions`,
        );

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch reasons",
          "DirectoryHttpApi.getReasons",
          error instanceof Error ? error : new Error(String(error)),
          [{ key: "participantIdPublic", value: params.participantIdPublic }],
        );
        throw error;
      }
    },

    async getConnectionRequests(
      params: GetConnectionRequestsParams,
    ): Promise<readonly unknown[]> {
      try {
        const direction = params.direction ?? "received";

        // BOC-578: received fetches only active (PENDING/SENT/SEEN) so
        // accepted/declined don't persist past refresh; sent also includes accepted (Connected tag).
        const includeAccepted = direction === "sent";
        const queryParams = new URLSearchParams({ direction });
        if (includeAccepted) queryParams.set("includeAccepted", "true");
        if (params.sortBy !== undefined) {
          queryParams.set("sortBy", params.sortBy);
        }
        if (params.sortOrder !== undefined) {
          queryParams.set("sortOrder", params.sortOrder);
        }
        const response = await introsOrEmpty(
          apiClient.get<Paginated<unknown>>(
            `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros?${queryParams.toString()}`,
          ),
        );

        return response.data.items;
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch connection requests",
          "DirectoryHttpApi.getConnectionRequests",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "eventIdPublic", value: params.eventIdPublic },
            { key: "direction", value: params.direction ?? "received" },
          ],
        );
        throw error;
      }
    },

    async requestIntro(params: RequestIntroParams): Promise<unknown> {
      try {
        const messageAsLocalizedText =
          params.message !== undefined && params.message.trim().length > 0
            ? { lang: LocaleCode.EN, content: params.message.trim() }
            : null;

        const body = {
          type: "intro" as const,
          targetParticipantIdPublic: params.targetParticipantIdPublic,
          message: messageAsLocalizedText,
          origin: params.origin,
          originData: params.originData,
        };

        const response = await apiClient.post<unknown>(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros`,
          body,
        );

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to request intro",
          "DirectoryHttpApi.requestIntro",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "eventIdPublic", value: params.eventIdPublic },
            {
              key: "targetParticipantIdPublic",
              value: params.targetParticipantIdPublic,
            },
          ],
        );
        throw error;
      }
    },

    async respondToRequest(params: RespondToRequestParams): Promise<unknown> {
      try {
        const status = params.action === "accept" ? "accepted" : "declined";

        const response = await apiClient.post<unknown>(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros/${encodeURIComponent(params.requestIdPublic)}/status`,
          { status },
        );

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to respond to request",
          "DirectoryHttpApi.respondToRequest",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "requestIdPublic", value: params.requestIdPublic },
            { key: "action", value: params.action },
          ],
        );
        throw error;
      }
    },

    async markIntroSeen(params: MarkIntroSeenParams): Promise<unknown> {
      try {
        const response = await apiClient.post<unknown>(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros/${encodeURIComponent(params.requestIdPublic)}/status`,
          { status: "seen" },
        );

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to mark intro as seen",
          "DirectoryHttpApi.markIntroSeen",
          error instanceof Error ? error : new Error(String(error)),
          [{ key: "requestIdPublic", value: params.requestIdPublic }],
        );
        throw error;
      }
    },

    async getContactInfo(params: GetContactInfoParams): Promise<unknown> {
      try {
        const response = await apiClient.get(
          `/api/v1/events/${encodeURIComponent(EventIdPublic.unwrap(params.eventIdPublic))}/intros/${encodeURIComponent(ConnectionRequestIdPublic.unwrap(params.requestIdPublic))}/contact-info`,
        );

        return response.data;
      } catch (error) {
        applicationLogger.error(
          "Failed to fetch contact info",
          "DirectoryHttpApi.getContactInfo",
          error instanceof Error ? error : new Error(String(error)),
          [
            { key: "eventIdPublic", value: params.eventIdPublic },
            { key: "requestIdPublic", value: params.requestIdPublic },
          ],
        );
        throw error;
      }
    },
  };
}
